// Client-error reporting (ED-2527).
//
// Ships throttled error reports to the PUBLIC /monitoring/client-errors
// collector on the client API, which re-emits each one through the backend log
// pipeline (CloudWatch → Better Stack) so app crashes appear in the Admin
// Console → System Health feed next to lambda errors, tagged with platform /
// screen / app version. Complements — does not replace — the Slack webhook in
// components/ErrorBoundary.
//
// Two entry points:
//   reportClientError(err, { source }) — called by the ErrorBoundary and the
//     global handlers; throttled per error signature so a render loop or a
//     rejection storm can't flood the collector.
//   installGlobalErrorHandlers() — installed once at app startup (App.tsx).
//     Wraps RN's ErrorUtils global handler (native + RN-web) and, on web,
//     window 'error' + 'unhandledrejection' — the classes of failure nothing
//     caught before this existed.
//
// The endpoint is unauthenticated by design (crashes can pre-date login, and a
// broken token refresh must still be reportable); the store/navigation lookups
// are lazy + best-effort so the reporter can never itself throw or create an
// import cycle.

import config from '../config';
import { isWeb } from './platform';
import { getAppVersion, getPlatformDevice } from './devicePlatform';

type ReportSource = 'boundary' | 'global' | 'promise';

const MAX_REPORTS_PER_SESSION = 20;
const SIGNATURE_COOLDOWN_MS = 60_000;

let sentCount = 0;
const lastSentBySignature = new Map<string, number>();
let installed = false;

function sessionContext(): { cognito_id?: string; account_id?: number | string; screen?: string } {
  const ctx: { cognito_id?: string; account_id?: number | string; screen?: string } = {};
  try {
    const { store } = require('../store');
    const state = store.getState();
    const cid = state?.authentication?.user?.cognito_id;
    if (cid) ctx.cognito_id = cid;
    const aid = state?.session?.account?.account_id;
    if (aid != null) ctx.account_id = aid;
  } catch {
    // store not ready (module-init crash) — report without identity
  }
  try {
    const { navigationRef } = require('../navigation/navigationRef');
    if (navigationRef?.isReady?.()) ctx.screen = navigationRef.getCurrentRoute()?.name;
  } catch {
    // navigation not mounted yet
  }
  return ctx;
}

export function reportClientError(
  err: unknown,
  opts: { source: ReportSource; screen?: string; componentStack?: string },
): void {
  try {
    const isError = err instanceof Error;
    const name = ((isError && err.name) || 'Error').slice(0, 120);
    const rawMessage = isError
      ? err.message
      : typeof err === 'string'
        ? err
        : (() => { try { return JSON.stringify(err); } catch { return String(err); } })();
    const message = String(rawMessage ?? 'Unknown error').slice(0, 600);

    // Per-signature cooldown + session cap: a crash loop reports once a minute,
    // not once a frame.
    const signature = `${name}:${message.slice(0, 160)}`;
    const now = Date.now();
    if (sentCount >= MAX_REPORTS_PER_SESSION) return;
    if (now - (lastSentBySignature.get(signature) || 0) < SIGNATURE_COOLDOWN_MS) return;
    lastSentBySignature.set(signature, now);
    sentCount += 1;

    // Mirror into the session-telemetry journey stream (post-throttle, so a
    // crash loop marks the journey once a minute, not once a frame). Lazy +
    // best-effort for the same reasons as the store/navigation lookups below.
    try {
      const { recorder } = require('../services/sessionTelemetry/recorder');
      recorder.recordError(name, message, opts.source);
    } catch { /* recorder unavailable — never block reporting */ }

    const ctx = sessionContext();
    const stack = [isError ? err.stack : null, opts.componentStack]
      .filter(Boolean)
      .join('\n')
      .slice(0, 4000);

    fetch(`${config.apiGateway.endpoint}/monitoring/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        events: [{
          name,
          message,
          stack: stack || null,
          source: opts.source,
          platform: getPlatformDevice(),
          screen: opts.screen ?? ctx.screen ?? null,
          app_version: getAppVersion(),
          cognito_id: ctx.cognito_id ?? null,
          account_id: ctx.account_id ?? null,
        }],
      }),
    }).catch(() => { /* fire-and-forget — never surface reporting failures */ });
  } catch {
    // The reporter runs inside error paths; it must never throw.
  }
}

export function installGlobalErrorHandlers(): void {
  if (installed) return;
  installed = true;

  // RN global JS handler (native + RN-web). Chain the previous handler so dev
  // redbox / default fatal behavior is preserved.
  try {
    const ErrorUtils = (global as any)?.ErrorUtils;
    if (ErrorUtils?.setGlobalHandler) {
      const prev = ErrorUtils.getGlobalHandler?.();
      ErrorUtils.setGlobalHandler((error: unknown, isFatal?: boolean) => {
        reportClientError(error, { source: 'global' });
        if (typeof prev === 'function') prev(error, isFatal);
      });
    }
  } catch {
    // ErrorUtils shape differs (tests) — skip
  }

  // Web only: uncaught window errors + unhandled promise rejections (RN has no
  // built-in hook for the latter; the boundary + global handler cover native).
  if (isWeb && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('error', (ev: ErrorEvent) => {
      if (ev?.error) reportClientError(ev.error, { source: 'global' });
    });
    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      reportClientError(ev?.reason ?? 'Unhandled promise rejection', { source: 'promise' });
    });
  }
}
